import { useEffect, useState } from "react";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { DataState } from "../components/ui/DataState";
import { ORG_TYPES, type Organization } from "../lib/types";

export default function Organizations() {
  const api = useApi();
  const { user } = useCurrentUser();
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const canCreate =
    user?.role === "platform_admin" || user?.role === "organization_admin";

  const load = () => {
    setLoading(true);
    return api
      .get<{ organizations: Organization[] }>("/api/organizations")
      .then((r) => {
        setOrgs(r.organizations);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">
            Organization profiles
          </h1>
          <p className="mt-2 text-slate-300">
            Black-owned businesses, churches, schools, community centers, and
            nonprofits using Community Shield OS.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreate((s) => !s)} variant="secondary">
            {showCreate ? "Cancel" : "Add organization"}
          </Button>
        )}
      </header>

      {showCreate && canCreate && (
        <CreateOrgForm
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}

      <div className="mt-8">
        <DataState
          loading={loading}
          error={error}
          data={orgs}
          empty={!!orgs && orgs.length === 0}
          loadingMessage="Loading organizations…"
          emptyMessage={
            canCreate
              ? "No organizations yet. Use “Add organization” to register the first one."
              : "No organizations yet. Once an admin adds them, they'll show up here."
          }
        >
          {(items) => (
            <div className="grid gap-5 md:grid-cols-2">
              {items.map((o) => (
                <Card key={o.id}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge>{o.type.replace("_", " ")}</Badge>
                    {o.neighborhood && <Badge>{o.neighborhood}</Badge>}
                    {o.city && <Badge>{o.city}</Badge>}
                  </div>
                  <CardTitle className="mt-3 text-lg">{o.name}</CardTitle>
                  <dl className="mt-3 grid gap-2 text-sm text-slate-300">
                    {o.contact_name && (
                      <div>
                        <dt className="text-slate-400">Main contact</dt>
                        <dd>{o.contact_name}</dd>
                      </div>
                    )}
                    {o.safety_email && (
                      <div>
                        <dt className="text-slate-400">Safety contact</dt>
                        <dd className="break-all">{o.safety_email}</dd>
                      </div>
                    )}
                    {o.hours && (
                      <div>
                        <dt className="text-slate-400">Hours</dt>
                        <dd>{o.hours}</dd>
                      </div>
                    )}
                  </dl>
                </Card>
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}

function CreateOrgForm({ onCreated }: { onCreated: () => void }) {
  const api = useApi();
  const [form, setForm] = useState({
    name: "",
    type: "business",
    address: "",
    city: "",
    neighborhood: "",
    contact_name: "",
    contact_email: "",
    safety_email: "",
    hours: "",
    emergency_instructions: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set =
    (k: keyof typeof form) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/organizations", form);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="surface mt-6 grid gap-5 p-6 md:grid-cols-2"
    >
      <Input
        required
        label="Name"
        name="name"
        value={form.name}
        onChange={set("name")}
      />
      <Select
        label="Type"
        name="type"
        value={form.type}
        onChange={set("type")}
      >
        {ORG_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </Select>
      <Input
        label="Address"
        name="address"
        value={form.address}
        onChange={set("address")}
      />
      <Input
        label="City"
        name="city"
        value={form.city}
        onChange={set("city")}
      />
      <Input
        label="Neighborhood"
        name="neighborhood"
        value={form.neighborhood}
        onChange={set("neighborhood")}
      />
      <Input
        label="Main contact name"
        name="contact_name"
        value={form.contact_name}
        onChange={set("contact_name")}
      />
      <Input
        label="Main contact email"
        name="contact_email"
        type="email"
        value={form.contact_email}
        onChange={set("contact_email")}
      />
      <Input
        label="Safety contact email"
        name="safety_email"
        type="email"
        value={form.safety_email}
        onChange={set("safety_email")}
      />
      <Input
        label="Hours"
        name="hours"
        value={form.hours}
        onChange={set("hours")}
      />
      <Textarea
        label="Emergency instructions"
        name="emergency_instructions"
        value={form.emergency_instructions}
        onChange={set("emergency_instructions")}
        className="md:col-span-2"
      />
      <Textarea
        label="Notes"
        name="notes"
        value={form.notes}
        onChange={set("notes")}
        className="md:col-span-2"
      />
      {error && (
        <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 md:col-span-2">
          {error}
        </p>
      )}
      <div className="flex justify-end md:col-span-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save organization"}
        </Button>
      </div>
    </form>
  );
}
